// ui/pages/GovCorrespondencePage.tsx
// ✅ 全文覆蓋版（以你提供的 UI 正確版本為底，最小變更加回 Step 2.1 功能）
// Step 2.1 補強：
// 1) 兩分頁新增表單補上「檔案上傳」（Firebase Storage 上傳 + Firestore 記錄 metadata）
// 2) 清單補上「檢視 / 編輯 / 刪除」操作鍵（刪除僅超管可見可用）
// 3) 維持既有 UI 架構（tab、toolbar、清單 grid、modal 版面）不換 layout
// 4) 仍維持：不建 composite index（只 where(module)，排序/篩選走前端）
// 5) 函覆自動產生函覆文號（可手動改）

import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataToolbar } from "../components/DataToolbar";
import { useAuth } from "../context/AuthContext";
import {
  db,
  storage,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "../../firebaseClient";
import { analyzeUploadedFile } from "../services/aiService";
import { useToast } from "../components/Toast";
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../hooks/useApprovalFlow";
import { createOrResetApprovalFlow, resetFlowForResubmission, updateStageDecision } from "../services/approvalFlowService";
import type { ApprovalStageKey } from "../types/approval";

type Direction = "INBOUND" | "OUTBOUND";

type Attachment = {
  name: string;
  url: string;
  path: string;
  size?: number;
  type?: string;
  uploadedAt?: string; // ISO
  uploadedBy?: string;
};

type GovItem = {
  id: string;
  module: string; // OFFICIAL_REPLY
  direction: Direction;
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "REJECTED" | "RETURNED" | string;

  docNo?: string; // 來文：發文字號 / 函覆：函覆文號
  fromOrg?: string; // 來文：發文機關
  toUnit?: string; // 來文：受文單位 / 函覆：受文單位
  ownerDept?: string;
  handler?: string;

  subject?: string;
  summary?: string;
  impact?: string;

  urgency?: "NORMAL" | "URGENT" | string;
  secrecy?: "NORMAL" | "CONFIDENTIAL" | string;

  // Outbound: optional relation to inbound doc no
  relatedInboundNo?: string;

  attachments?: Attachment[];

  createdAt?: string; // ISO
  updatedAt?: string; // ISO
  createdBy?: string;

  [k: string]: any;
};

const COLLECTION = "approval_requests";
const MODULE = "OFFICIAL_REPLY";

// ✅ 超管名單：刪除僅此群可用（最小依賴，避免引入別檔造成風險）
const SUPER_USERS = new Set(["kc@lg.app", "ad@lg.app", "ops@lg.app", "gm@lg.app"]);
function isSuperUser(email?: string | null) {
  if (!email) return false;
  return SUPER_USERS.has(String(email).toLowerCase());
}

function nowIso() {
  return new Date().toISOString();
}

// Firestore Timestamp 兼容：把 string / Date / Timestamp / seconds 轉成毫秒，避免 localeCompare 爆掉
function toMillis(v: any): number {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  if (v instanceof Date) return v.getTime();
  if (typeof v?.toDate === "function") {
    try {
      const d = v.toDate();
      return d instanceof Date ? d.getTime() : 0;
    } catch {
      // ignore
    }
  }
  if (typeof v?.seconds === "number") return v.seconds * 1000;
  if (typeof v?._seconds === "number") return v._seconds * 1000;
  return 0;
}

function toIso(v: any): string | undefined {
  const ms = toMillis(v);
  if (!ms) return undefined;
  try {
    return new Date(ms).toISOString();
  } catch {
    return undefined;
  }
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

// ✅ 新增函覆自動產生函覆文號（不需要查 DB/不需要 index）
function genOutboundNo() {
  const d = new Date();
  const ymd = `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
  const hms = `${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
  const rnd = Math.floor(Math.random() * 900) + 100;
  return `OUT-${ymd}-${hms}-${rnd}`;
}

function statusLabel(s?: string) {
  const map: Record<string, string> = {
    DRAFT: "草稿",
    IN_REVIEW: "簽核中",
    APPROVED: "已核准",
    FULLY_APPROVED: "已核准（完成）",
    COMPLETED: "已完成",
    REJECTED: "已駁回",
    RETURNED: "退回",
  };
  return s ? map[s] || s : "";
}

function toCsvCell(v: any) {
  const s = String(v ?? "").replace(/\r?\n/g, " ").replace(/"/g, '""');
  return `"${s}"`;
}

function downloadTextFile(filename: string, content: string, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SimpleModal(props: { open: boolean; title: string; onClose: () => void; children: React.ReactNode }) {
  if (!props.open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}
      onMouseDown={props.onClose}
    >
      <div
        style={{
          width: "min(980px, 96vw)",
          maxHeight: "86vh",
          overflow: "auto",
          background: "rgba(20,20,20,0.98)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 14,
          padding: 16,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{props.title}</div>
          <button className="btn" style={{ width: "auto", marginTop: 0 }} onClick={props.onClose}>
            關閉
          </button>
        </div>
        <div style={{ marginTop: 12 }}>{props.children}</div>
      </div>
    </div>
  );
}

function sanitizeFileName(name: string) {
  return name.replace(/[^\w.\-() \u4e00-\u9fa5]+/g, "_").slice(0, 180);
}

async function uploadAttachments(params: {
  docId: string;
  files: File[];
  uploadedBy: string;
}): Promise<Attachment[]> {
  const { docId, files, uploadedBy } = params;
  if (!files.length) return [];
  const uploaded: Attachment[] = [];

  for (const f of files) {
    const safeName = sanitizeFileName(f.name);
    const path = `gov_correspondence/${docId}/${Date.now()}_${safeName}`;
    // 使用 compat storage（firebaseClient 匯出的 storage）
    const r = (storage as any).ref().child(path);
    const snap = await r.put(f);
    const url = await snap.ref.getDownloadURL();

    uploaded.push({
      name: f.name,
      url,
      path,
      size: f.size,
      type: f.type,
      uploadedAt: nowIso(),
      uploadedBy,
    });
  }

  return uploaded;
}

export default function GovCorrespondencePage() {
  const { user } = useAuth();
  const superUser = isSuperUser(user?.email);
  const { showToast } = useToast();

  const [direction, setDirection] = useState<Direction>("INBOUND");
  const [items, setItems] = useState<GovItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [qText, setQText] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState<GovItem | null>(null);

  const [flowOpen, setFlowOpen] = useState(false);
  const [flowTarget, setFlowTarget] = useState<GovItem | null>(null);
  const { flow: flowData, loading: flowLoading } = useApprovalFlow(MODULE, flowTarget?.id || "");

  const [form, setForm] = useState<Partial<GovItem>>({
    direction: "INBOUND",
    status: "DRAFT",
    urgency: "NORMAL",
    secrecy: "NORMAL",
    attachments: [],
  });

  // 上傳：暫存待上傳檔案（save 時才 upload + update attachments）
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // ✅ 不建 index：只做 where(module) 把全部抓回來，排序/分頁/篩選放前端
  async function loadAllByModule() {
    setLoading(true);
    try {
      const col = collection(db as any, COLLECTION);
      const q = query(col, where("module", "==", MODULE));
      const snap = await getDocs(q);

      const rows: GovItem[] = [];
      snap.forEach((d) => rows.push({ id: d.id, ...(d.data() as any) }));

      // createdAt/updatedAt 可能是 Firestore Timestamp（物件），先正規化成 ISO 字串
      for (const r of rows as any[]) {
        if (r.createdAt && typeof r.createdAt !== "string") r.createdAt = toIso(r.createdAt) || "";
        if (r.updatedAt && typeof r.updatedAt !== "string") r.updatedAt = toIso(r.updatedAt) || "";
      }

      // 以毫秒排序（避免 localeCompare 對 Timestamp 物件爆掉）
      rows.sort((a, b) => toMillis((b as any).createdAt) - toMillis((a as any).createdAt));
      setItems(rows);
    } catch (e: any) {
      console.error(e);
      window.alert(`載入失敗：${e?.message || e}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAllByModule();
  }, []);

  const filtered = useMemo(() => {
    const kw = qText.trim().toLowerCase();

    let base = items.filter((it) => it.direction === direction);

    if (kw) {
      base = base.filter((it) => {
        const hay = [
          it.docNo,
          it.subject,
          it.summary,
          it.fromOrg,
          it.toUnit,
          it.ownerDept,
          it.handler,
          it.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(kw);
      });
    }

    return base;
  }, [items, direction, qText]);

  function resetUploadState() {
    setPendingFiles([]);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function openCreate() {
    resetUploadState();
    setForm({
      id: "",
      module: MODULE,
      direction,
      status: "DRAFT",
      urgency: "NORMAL",
      secrecy: "NORMAL",
      docNo: direction === "OUTBOUND" ? genOutboundNo() : "",
      attachments: [],
    });
    setOpen(true);
  }

  async function openView(id: string) {
    const found = items.find((x) => x.id === id);
    setViewItem(found || null);
    setViewOpen(true);
  }

  async function openEdit(id: string) {
    resetUploadState();
    const found = items.find((x) => x.id === id);
    if (!found) return;

    // 切到該筆方向（避免 tab 不一致造成誤會）
    setDirection(found.direction);
    setForm({
      ...found,
      attachments: Array.isArray(found.attachments) ? found.attachments : [],
    });
    setOpen(true);
  }

  async function doDelete(id: string) {
    if (!superUser) {
      window.alert("無權限：刪除僅限超管。");
      return;
    }
    if (!window.confirm("確定要刪除此筆資料？此動作不可復原。")) return;

    try {
      await deleteDoc(doc(db as any, COLLECTION, id));
      await loadAllByModule();
    } catch (e: any) {
      console.error(e);
      window.alert(`刪除失敗：${e?.message || e}`);
    }
  }

  function addPendingFiles(files: FileList | File[]) {
    const arr = Array.from(files || []);
    if (!arr.length) return;

    // 避免重複：同名+大小視為同檔（簡化）
    setPendingFiles((prev) => {
      const next = [...prev];
      for (const f of arr) {
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      return next;
    });
  }

  function removePendingFile(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function removeExistingAttachment(idx: number) {
    setForm((p) => {
      const cur = Array.isArray((p as any).attachments) ? ((p as any).attachments as Attachment[]) : [];
      const next = cur.filter((_, i) => i !== idx);
      return { ...p, attachments: next };
    });
  }

  async function handleAiDraftForForm() {
    if (aiLoading) return;

    try {
      setAiLoading(true);

      const ctx = {
        direction: (form.direction as any) || direction,
        docNo: form.docNo,
        fromOrg: form.fromOrg,
        toUnit: form.toUnit,
        ownerDept: form.ownerDept,
        handler: form.handler,
        subject: form.subject,
        urgency: form.urgency,
        secrecy: form.secrecy,
      };

      const res: any = await (analyzeUploadedFile as any)({
        mode: "summary",
	        featureId: "govCorrespondence.analysis",
        contextType: "GOV_CORRESPONDENCE",
        contextId: String(form.id || form.docNo || "new"),
        userInstruction:
          "請以此公文案件資訊與附件內容，協助撰寫『摘要』與『影響性評估』的草稿（可條列）。\n" +
          "案件資訊：" +
          JSON.stringify(ctx, null, 2),
	        rawFiles: pendingFiles,
	        files: pendingFiles,
      });

      const summaryDraft = String(res?.summary || "").trim();
	      const impactDraftBase = Array.isArray(res?.riskFlags)
	        ? res.riskFlags.map((x: string) => `• ${String(x).trim()}`).join("\n")
	        : "";
	      const riskEvalText = String(res?.riskAssessmentText || "").trim();
	      const impactDraft = [impactDraftBase, riskEvalText].filter(Boolean).join("\n\n");

      setForm((p) => {
        const next: any = { ...p };
        if (summaryDraft) {
          next.summary = String(p.summary || "").trim()
            ? `${String(p.summary).trim()}\n\n${summaryDraft}`
            : summaryDraft;
        }
        if (impactDraft) {
          next.impact = String(p.impact || "").trim()
            ? `${String(p.impact).trim()}\n\n${impactDraft}`
            : impactDraft;
        }
        return next;
      });

      showToast("AI 草稿已填入（摘要/影響性評估）", "success");
    } catch (e: any) {
      console.error(e);
      showToast(`AI 草稿失敗：${e?.message || e}`, "error");
    } finally {
      setAiLoading(false);
    }
  }

  async function saveDraftOrSubmit(nextStatus: "DRAFT" | "IN_REVIEW") {
    setSaving(true);
    try {
      const effectiveDirection = ((form.direction as any) || direction) as Direction;

      const submitNow = nextStatus === "IN_REVIEW";
      const prevStatus = String((form as any).status || "");
      const isResubmitting = submitNow && (prevStatus === "RETURNED" || prevStatus === "REJECTED");

      const actorUid = (user as any)?.id || (user as any)?.uid || "";
      const actorName = user?.displayName || user?.email || "User";

      const inputDocNo = String(form.docNo || "").trim();
      const docNoFinal =
        inputDocNo || (!form.id && effectiveDirection === "OUTBOUND" ? genOutboundNo() : "");

      const baseFields: Partial<GovItem> = {
        module: MODULE,
        direction: effectiveDirection,
        status: nextStatus,
        docNo: docNoFinal,
        // 讓 True Todo / 待辦同步能抓到名稱與文號
        title: String(form.subject || "").trim(),
        docCode: docNoFinal,
        fromOrg: String(form.fromOrg || "").trim(),
        toUnit: String(form.toUnit || "").trim(),
        ownerDept: String(form.ownerDept || "").trim(),
        handler: String(form.handler || "").trim(),
        subject: String(form.subject || "").trim(),
        summary: String(form.summary || "").trim(),
        impact: String(form.impact || "").trim(),
        urgency: ((form.urgency as any) || "NORMAL") as any,
        secrecy: ((form.secrecy as any) || "NORMAL") as any,
        relatedInboundNo: String((form as any).relatedInboundNo || "").trim(),
      };

      if (!baseFields.subject) {
        window.alert("請先填寫「主旨」。");
        return;
      }

      const existingAttachments: Attachment[] = Array.isArray((form as any).attachments)
        ? ((form as any).attachments as Attachment[])
        : [];

      const editorEmail = user?.email || "";

      const shouldEnsureFlow = submitNow && effectiveDirection === "OUTBOUND";

      // === EDIT ===
      if (form.id) {
        const docId = String(form.id);

        // 先上傳新檔（若有），再合併 attachments
        let uploaded: Attachment[] = [];
        if (pendingFiles.length) {
          uploaded = await uploadAttachments({ docId, files: pendingFiles, uploadedBy: editorEmail });
        }
        const mergedAttachments = [...existingAttachments, ...uploaded];

        const updatePayload: any = {
          ...baseFields,
          attachments: mergedAttachments,
          updatedAt: nowIso(),
          ...(shouldEnsureFlow
            ? {
                approvalFlowId: `${MODULE}_${docId}`,
                ...(isResubmitting
                  ? {
                      resubmittedAt: nowIso(),
                      resubmittedBy: actorName,
                    }
                  : {}),
              }
            : {}),
        };

        await updateDoc(doc(db as any, COLLECTION, docId), updatePayload);

        // Step 3/4/5：OUTBOUND 送呈核 -> 建立/重置簽核流程（沿用合約簽核新制）
        if (shouldEnsureFlow) {
          const flowId = `${MODULE}_${docId}`;
          try {
            if (isResubmitting) {
              await resetFlowForResubmission(flowId);
              showToast("已重置簽核流程並重新送呈核", "success");
            } else {
              await createOrResetApprovalFlow(
                MODULE as any,
                docId,
                actorUid,
                String(baseFields.ownerDept || "").trim(),
                String(baseFields.handler || "").trim() || actorName
              );
              showToast("已建立簽核流程並送呈核", "success");
            }
          } catch (e) {
            // 若簽核流程建立失敗，避免使用者被卡在 IN_REVIEW 無法編輯，直接退回草稿
            try {
              await updateDoc(doc(db as any, COLLECTION, docId), {
                status: "DRAFT",
                updatedAt: nowIso(),
              } as any);
            } catch {
              // ignore
            }
            showToast("建立簽核流程失敗，已自動改回草稿。請稍後再試。", "error");
            await loadAllByModule();
            return;
          }
        }

        setOpen(false);
        resetUploadState();
        await loadAllByModule();
        return;
      }

      // === CREATE ===
      const payload: any = {
        id: "",
        ...baseFields,
        attachments: existingAttachments, // 先寫入既有（通常為空），稍後再 update
        createdAt: nowIso(),
        updatedAt: nowIso(),
        createdBy: editorEmail,
      };

      const col = collection(db as any, COLLECTION);
      const docRef = await addDoc(col, payload);

      const docId = docRef.id;

      // 上傳待上傳檔案（若有）
      let uploaded: Attachment[] = [];
      if (pendingFiles.length) {
        uploaded = await uploadAttachments({ docId, files: pendingFiles, uploadedBy: editorEmail });
      }
      const mergedAttachments = [...existingAttachments, ...uploaded];

      // 最小 update：補 id + attachments + updatedAt
      await updateDoc(doc(db as any, COLLECTION, docId), {
        id: docId,
        attachments: mergedAttachments,
        updatedAt: nowIso(),
        ...(shouldEnsureFlow ? { approvalFlowId: `${MODULE}_${docId}` } : {}),
      });

      if (shouldEnsureFlow) {
        try {
          await createOrResetApprovalFlow(
            MODULE as any,
            docId,
            actorUid,
            String(baseFields.ownerDept || "").trim(),
            String(baseFields.handler || "").trim() || actorName
          );
          showToast("已建立簽核流程並送呈核", "success");
        } catch (e) {
          // 若簽核流程建立失敗，避免使用者被卡在 IN_REVIEW 無法編輯，直接退回草稿
          try {
            await updateDoc(doc(db as any, COLLECTION, docId), {
              status: "DRAFT",
              updatedAt: nowIso(),
            } as any);
          } catch {
            // ignore
          }
          showToast("建立簽核流程失敗，已自動改回草稿。請稍後再試。", "error");
        }
      }

      setOpen(false);
      resetUploadState();
      await loadAllByModule();
    } catch (e: any) {
      console.error(e);
      window.alert(`儲存失敗：${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  }

  function exportCsv() {
    if (!filtered.length) {
      window.alert("目前沒有資料可匯出。");
      return;
    }

    const headers = [
      "direction",
      "status",
      "docNo",
      "fromOrg",
      "toUnit",
      "ownerDept",
      "handler",
      "subject",
      "summary",
      "impact",
      "urgency",
      "secrecy",
      "createdAt",
      "createdBy",
      "attachmentsCount",
    ];

    const csv =
      "\ufeff" +
      headers.map(toCsvCell).join(",") +
      "\n" +
      filtered
        .map((r) =>
          headers
            .map((h) => {
              if (h === "attachmentsCount") return toCsvCell((r.attachments || []).length);
              return toCsvCell((r as any)[h]);
            })
            .join(",")
        )
        .join("\n");

    const fname = `gov_${direction.toLowerCase()}_${nowIso().slice(0, 10)}.csv`;
    downloadTextFile(fname, csv, "text/csv;charset=utf-8");
  }

  const canEdit = !!user?.email;

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    width: "auto",
    marginTop: 0,
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.10)",
    background: active ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.90)",
  });

  const toolbarExtras = (
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <input
        className="input"
        style={{ width: 320 }}
        value={qText}
        placeholder="搜尋（文號/主旨/機關/承辦人…）"
        onChange={(e) => setQText(e.target.value)}
      />
      <button className="btn" style={{ width: "auto", marginTop: 0 }} onClick={exportCsv}>
        匯出
      </button>
    </div>
  );

  const actionBtnStyle: React.CSSProperties = {
    width: "auto",
    marginTop: 0,
    padding: "6px 10px",
    borderRadius: 10,
    fontSize: 12,
    lineHeight: "14px",
  };

  // Step 4/5：簽核面板送出決策（沿用 approvalFlowService 的統一邏輯）
  async function handleFlowDecision(
    stageKey: ApprovalStageKey,
    decision: "APPROVED" | "REJECTED" | "RETURNED" | "DONE" | "APPROVE_AND_SEAL",
    comment?: string,
    deptCode?: string,
    isDirectSign?: boolean
  ) {
    if (!flowTarget?.id) return;

    const flowId = `${MODULE}_${flowTarget.id}`;
    const actorUid = (user as any)?.id || (user as any)?.uid || "";
    const actorName = user?.displayName || user?.email || "User";
    const actorEmail = user?.email || "";

    // normalize decision (ApprovalFlowPanel 可能傳 APPROVE_AND_SEAL)
    const normalized = decision === "APPROVE_AND_SEAL" ? "APPROVED" : decision;

    try {
      await updateStageDecision({
        flowId,
        stageKey,
        decision: normalized as any,
        comment: String(comment || "").trim(),
        actorUid,
        actorName,
        actorEmail,
        deptCode,
        isDirectSign,
      } as any);
      showToast("已送出簽核決策", "success");
    } catch (e: any) {
      showToast(e?.message ? `簽核決策送出失敗：${e.message}` : "簽核決策送出失敗", "error");
      throw e;
    }

    // 刷新清單（讓狀態由 IN_REVIEW -> FULLY_APPROVED/RETURNED/REJECTED 即時反映）
    await loadAllByModule();
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        <div style={{ fontSize: 26, fontWeight: 900 }}>公文來文與函覆管理</div>
        <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>主管機關來函・表處與公文回覆追蹤。</div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn" style={tabBtnStyle(direction === "INBOUND")} onClick={() => setDirection("INBOUND")}>
            公文來文 (Inbound)
          </button>
          <button className="btn" style={tabBtnStyle(direction === "OUTBOUND")} onClick={() => setDirection("OUTBOUND")}>
            公文函覆 (Outbound)
          </button>
        </div>

        <DataToolbar
          title={null}
          canEdit={canEdit}
          onCreate={openCreate}
          createLabel="新增資料"
          extraButtons={toolbarExtras}
        />
      </div>

      <div style={{ marginTop: 6 }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
          {direction === "INBOUND" ? "公文來文清單" : "公文函覆進度"}
        </div>

        <div
          style={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            overflow: "hidden",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          {/* ✅ 只加一欄「操作」：保持既有 grid 風格 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "160px 120px 1fr 180px 140px 220px",
              gap: 0,
              padding: "10px 12px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.65)",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <div>文號</div>
            <div>狀態</div>
            <div>主旨</div>
            <div>對象/機關</div>
            <div>建立時間</div>
            <div>操作</div>
          </div>

          {loading ? (
            <div style={{ padding: 14, color: "rgba(255,255,255,0.65)" }}>載入中…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 18, color: "rgba(255,255,255,0.45)" }}>
              {direction === "INBOUND" ? "目前無來文紀錄" : "目前無函覆紀錄"}
            </div>
          ) : (
            filtered.map((it) => (
              <div
                key={it.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 120px 1fr 180px 140px 220px",
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  alignItems: "center",
                  fontSize: 13,
                }}
              >
                <div style={{ color: "rgba(255,255,255,0.9)" }}>{it.docNo || "—"}</div>
                <div style={{ color: "rgba(255,255,255,0.75)" }}>{statusLabel(it.status) || "—"}</div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.9)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {it.subject || "—"}
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {direction === "INBOUND" ? it.fromOrg || "—" : it.toUnit || "—"}
                </div>
                <div style={{ color: "rgba(255,255,255,0.60)" }}>
                  {it.createdAt ? new Date(it.createdAt).toLocaleString() : "—"}
                </div>

                {/* ✅ 操作鍵：檢視 / 編輯 / 刪除（刪除僅超管可用） */}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-start", alignItems: "center" }}>
                  <button
                    className="btn"
                    style={actionBtnStyle}
                    onClick={() => openView(it.id)}
                    title="檢視"
                  >
                    👁 檢視
                  </button>
                  <button
                    className="btn"
                    style={actionBtnStyle}
                    onClick={() => openEdit(it.id)}
                    title="編輯"
                  >
                    ✏ 編輯
                  </button>

                  {it.direction === "OUTBOUND" ? (
                    <button
                      className="btn"
                      style={actionBtnStyle}
                      onClick={() => { setFlowTarget(it); setFlowOpen(true); }}
                      title="簽核流程"
                    >
                      🧾 簽核流程
                    </button>
                  ) : null}

                  {superUser ? (
                    <button
                      className="btn"
                      style={{ ...actionBtnStyle, color: "var(--danger)" }}
                      onClick={() => doDelete(it.id)}
                      title="刪除"
                    >
                      🗑 刪除
                    </button>
                  ) : (
                    <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>（刪除限超管）</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>



      {/* 簽核流程 Modal（函覆 OUTBOUND） */}
      <SimpleModal
        open={flowOpen}
        title="簽核流程"
        onClose={() => {
          setFlowOpen(false);
          setFlowTarget(null);
        }}
      >
        {!flowTarget ? (
          <div style={{ color: "rgba(255,255,255,0.6)" }}>—</div>
        ) : flowLoading ? (
          <div style={{ color: "rgba(255,255,255,0.6)" }}>讀取簽核流程中…</div>
        ) : !flowData ? (
          <div style={{ color: "rgba(255,255,255,0.6)" }}>尚未建立簽核流程（可能尚未送出呈核）。</div>
        ) : (
          <ApprovalFlowPanel
            flow={flowData as any}
            mode="action"
            showStages={true}
            onDecision={handleFlowDecision as any}
          />
        )}
      </SimpleModal>

      {/* 檢視 Modal（只讀，不改 UI 結構，純展示） */}
      <SimpleModal
        open={viewOpen}
        title={viewItem?.direction === "OUTBOUND" ? "檢視函覆資料" : "檢視來文資料"}
        onClose={() => setViewOpen(false)}
      >
        {!viewItem ? (
          <div style={{ color: "rgba(255,255,255,0.6)" }}>—</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field">
              <label className="label">{viewItem.direction === "INBOUND" ? "發文字號" : "函覆文號"}</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.docNo || "—"}
              </div>
            </div>
            <div className="field">
              <label className="label">主旨</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.subject || "—"}
              </div>
            </div>

            <div className="field">
              <label className="label">{viewItem.direction === "INBOUND" ? "發文機關" : "受文單位"}</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.direction === "INBOUND" ? viewItem.fromOrg || "—" : viewItem.toUnit || "—"}
              </div>
            </div>
            <div className="field">
              <label className="label">{viewItem.direction === "INBOUND" ? "受文單位" : "對應來文字號"}</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.direction === "INBOUND"
                  ? viewItem.toUnit || "—"
                  : viewItem.relatedInboundNo || "—"}
              </div>
            </div>

            <div className="field">
              <label className="label">主責部門</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.ownerDept || "—"}
              </div>
            </div>
            <div className="field">
              <label className="label">承辦人</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.handler || "—"}
              </div>
            </div>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label className="label">摘要</label>
              <div className="input" style={{ minHeight: 72, opacity: 0.9, whiteSpace: "pre-wrap" }}>
                {viewItem.summary || "—"}
              </div>
            </div>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label className="label">影響性評估</label>
              <div className="input" style={{ minHeight: 56, opacity: 0.9, whiteSpace: "pre-wrap" }}>
                {viewItem.impact || "—"}
              </div>
            </div>

            <div className="field">
              <label className="label">急件/普件</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.urgency || "—"}
              </div>
            </div>
            <div className="field">
              <label className="label">密等</label>
              <div className="input" style={{ opacity: 0.9 }}>
                {viewItem.secrecy || "—"}
              </div>
            </div>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label className="label">附檔</label>
              {Array.isArray(viewItem.attachments) && viewItem.attachments.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {viewItem.attachments.map((a, idx) => (
                    <div key={`${a.path}_${idx}`} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "rgba(255,255,255,0.85)", textDecoration: "underline" }}
                      >
                        {a.name}
                      </a>
                      <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
                        {a.size ? `${Math.round(a.size / 1024)} KB` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "rgba(255,255,255,0.45)" }}>（無附檔）</div>
              )}
            </div>
          </div>
        )}
      </SimpleModal>

      {/* 新增/編輯 Modal（保留原 UI 結構，僅加上傳區塊與編輯分支） */}
      <SimpleModal
        open={open}
        title={
          form.id
            ? (String(form.direction || direction) === "OUTBOUND" ? "編輯函覆資料" : "編輯來文資料")
            : (direction === "INBOUND" ? "新增來文資料" : "新增函覆資料")
        }
        onClose={() => {
          if (!saving) setOpen(false);
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field">
            <label className="label">{(form.direction || direction) === "INBOUND" ? "發文字號" : "函覆文號"}</label>
            <input
              className="input"
              value={form.docNo || ""}
              onChange={(e) => setForm((p) => ({ ...p, docNo: e.target.value }))}
              placeholder={(form.direction || direction) === "INBOUND" ? "例如：府授環水字第xxxx號" : "系統自動產生，可自行修改"}
            />
          </div>

          <div className="field">
            <label className="label">主旨（必填）</label>
            <input
              className="input"
              value={form.subject || ""}
              onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              placeholder="例如：貴公司請就…於xx日前回覆"
            />
          </div>

          {(form.direction || direction) === "INBOUND" ? (
            <>
              <div className="field">
                <label className="label">發文機關</label>
                <input
                  className="input"
                  value={form.fromOrg || ""}
                  onChange={(e) => setForm((p) => ({ ...p, fromOrg: e.target.value }))}
                  placeholder="例如：台北市政府…"
                />
              </div>
              <div className="field">
                <label className="label">受文單位</label>
                <input
                  className="input"
                  value={form.toUnit || ""}
                  onChange={(e) => setForm((p) => ({ ...p, toUnit: e.target.value }))}
                  placeholder="例如：貴公司/某部門"
                />
              </div>
            </>
          ) : (
            <>
              <div className="field">
                <label className="label">受文單位</label>
                <input
                  className="input"
                  value={form.toUnit || ""}
                  onChange={(e) => setForm((p) => ({ ...p, toUnit: e.target.value }))}
                  placeholder="例如：主管機關…"
                />
              </div>
              <div className="field">
                <label className="label">對應來文字號（可留空）</label>
                <input
                  className="input"
                  value={(form as any).relatedInboundNo || ""}
                  onChange={(e) => setForm((p) => ({ ...(p as any), relatedInboundNo: e.target.value }))}
                  placeholder="例如：府授環水字第xxxx號"
                />
              </div>
            </>
          )}

          <div className="field">
            <label className="label">主責部門</label>
            <input
              className="input"
              value={form.ownerDept || ""}
              onChange={(e) => setForm((p) => ({ ...p, ownerDept: e.target.value }))}
              placeholder="例如：法務部 / 行銷部"
            />
          </div>

          <div className="field">
            <label className="label">承辦人</label>
            <input
              className="input"
              value={form.handler || ""}
              onChange={(e) => setForm((p) => ({ ...p, handler: e.target.value }))}
              placeholder="例如：Kevin"
            />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">說明 / 摘要</label>
            <textarea
              className="input"
              style={{ minHeight: 96 }}
              value={form.summary || ""}
              onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
              placeholder="重點、時程、應對方向…"
            />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">影響性評估（可留空）</label>
            <textarea
              className="input"
              style={{ minHeight: 72 }}
              value={form.impact || ""}
              onChange={(e) => setForm((p) => ({ ...p, impact: e.target.value }))}
              placeholder="對公司/營運/法遵/聲譽的影響…"
            />
          </div>

          <div className="field">
            <label className="label">急件 / 普件</label>
            <select
              className="input"
              value={form.urgency || "NORMAL"}
              onChange={(e) => setForm((p) => ({ ...p, urgency: e.target.value }))}
            >
              <option value="NORMAL">普件</option>
              <option value="URGENT">急件</option>
            </select>
          </div>

          <div className="field">
            <label className="label">密等</label>
            <select
              className="input"
              value={form.secrecy || "NORMAL"}
              onChange={(e) => setForm((p) => ({ ...p, secrecy: e.target.value }))}
            >
              <option value="NORMAL">普通</option>
              <option value="CONFIDENTIAL">密</option>
            </select>
          </div>

          {/* ✅ Step 2.1：檔案上傳（保持 modal 內、同樣 grid 風格） */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <label className="label" style={{ margin: 0 }}>附檔上傳</label>
              <button
                type="button"
                className="btn"
                style={{ width: "auto", marginTop: 0, padding: "4px 10px", fontSize: 12 }}
                onClick={handleAiDraftForForm}
                disabled={saving || aiLoading}
                title="AI：自動生成摘要 / 擬辦建議（可先不附檔，用既有欄位先生成）"
              >
                ✨ AI 草稿
              </button>
            </div>

            {/* 既有附檔（編輯時可移除 metadata） */}
            {Array.isArray((form as any).attachments) && (form as any).attachments.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                {(form as any).attachments.map((a: Attachment, idx: number) => (
                  <div key={`${a.path}_${idx}`} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "rgba(255,255,255,0.85)", textDecoration: "underline" }}
                    >
                      {a.name}
                    </a>
                    <button
                      type="button"
                      className="btn"
                      style={{ width: "auto", marginTop: 0, padding: "4px 8px", fontSize: 12 }}
                      onClick={() => removeExistingAttachment(idx)}
                      disabled={saving}
                      title="從此筆資料移除附件記錄（不刪 Storage 檔案）"
                    >
                      移除
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            {/* 待上傳檔案 */}
            {pendingFiles.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                {pendingFiles.map((f, idx) => (
                  <div key={`${f.name}_${f.size}_${idx}`} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ color: "rgba(255,255,255,0.85)" }}>{f.name}</span>
                    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
                      {Math.round(f.size / 1024)} KB
                    </span>
                    <button
                      type="button"
                      className="btn"
                      style={{ width: "auto", marginTop: 0, padding: "4px 8px", fontSize: 12 }}
                      onClick={() => removePendingFile(idx)}
                      disabled={saving}
                    >
                      移除
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => addPendingFiles(e.target.files || [])}
            />

            <div
              style={{
                border: "1px dashed rgba(255,255,255,0.22)",
                borderRadius: 12,
                padding: "18px 14px",
                textAlign: "center",
                background: dragOver ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.02)",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer?.files) addPendingFiles(e.dataTransfer.files);
              }}
              title="點擊選擇檔案，或拖曳檔案到此處"
            >
              <div style={{ fontWeight: 800, color: "rgba(255,255,255,0.88)", marginBottom: 6 }}>附檔上傳</div>
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                拖曳檔案到此，或點擊此區塊選擇
              </div>
            </div>

            <div style={{ marginTop: 8, color: "rgba(255,255,255,0.38)", fontSize: 12 }}>
              ※ 儲存/送呈核時才會上傳並寫入附件記錄
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
          <button
            className="btn"
            style={{ width: "auto", marginTop: 0 }}
            disabled={saving}
            onClick={() => setOpen(false)}
          >
            取消
          </button>

          {(form.direction || direction) === "OUTBOUND" ? (
            <>
              <button
                className="btn"
                style={{ width: "auto", marginTop: 0 }}
                disabled={saving}
                onClick={() => saveDraftOrSubmit("DRAFT")}
              >
                {saving ? "儲存中…" : "儲存草稿"}
              </button>
              <button
                className="btn"
                style={{ width: "auto", marginTop: 0 }}
                disabled={saving}
                onClick={() => saveDraftOrSubmit("IN_REVIEW")}
              >
                {saving ? "送出中…" : "送呈核"}
              </button>
            </>
          ) : (
            <button
              className="btn"
              style={{ width: "auto", marginTop: 0 }}
              disabled={saving}
              onClick={() => saveDraftOrSubmit("DRAFT")}
            >
              {saving ? "儲存中…" : "儲存"}
            </button>
          )}
        </div>
      </SimpleModal>
    </div>
  );
}